import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { message } from 'antd'
import { getFriendlyErrorMessage } from '../components/CreativeDirectionChat/utils'
import {
  advanceCreativeDirectionSession,
  createCreativeDirectionSession,
  finalizeCreativeDirectionSession,
  getCreativeDirectionGenerationStatus,
  getCreativeDirectionEntities,
  getCreativeDirectionSessionFull,
  getCreativeDirectionSessionStatus,
  saveCreativeDirectionVision,
  sendCreativeDirectionAgentChat,
  streamCreativeDirectionChat,
  synthesizeCreativeDirectionVision,
  uploadCreativeDirectionReference,
  uploadCreativeDirectionReferenceToPath,
  uploadCreativeDirectionUploads
} from '../../../../../services/api/aiVideoGeneration'
import { useTaskPoller } from '../../../hooks/useTaskPoller'

const MAX_REFERENCE_FILE_SIZE = 50 * 1024 * 1024
const GENERATION_POLL_INTERVAL = 15000
const CREATIVE_DIRECTION_SESSION_STORAGE_PREFIX = 'creative-direction-session'
const ACCEPTED_REFERENCE_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'webp',
  'gif',
  'mp4',
  'mov',
  'webm'
]

const MOVEMENTS = [
  { key: 'the_read', label: 'Read' },
  { key: 'emotional_core', label: 'Emotion' },
  { key: 'imagination', label: 'Imagery' },
  { key: 'camera', label: 'Camera' },
  { key: 'rhythm_mood_arc', label: 'Rhythm' },
  { key: 'references', label: 'References' },
  { key: 'anti_vision', label: 'Avoid' },
  { key: 'synthesis', label: 'Synthesis' }
]

const PHASE_STEPS = [
  { key: 'vision', label: 'Vision' },
  { key: 'characters', label: 'Characters' },
  { key: 'outfits', label: 'Outfits' },
  { key: 'locations', label: 'Locations' },
  { key: 'storyboard', label: 'Storyboard' },
  { key: 'finalized', label: 'Guideline' }
]

const getResponseData = response => response?.data?.data || response?.data || {}

const getCreativeDirectionSessionStorageKey = videoId =>
  `${CREATIVE_DIRECTION_SESSION_STORAGE_PREFIX}:${videoId || 'draft'}`

const getStoredCreativeDirectionSessionId = videoId => {
  if (typeof window === 'undefined' || !window.localStorage) return null

  try {
    return (
      window.localStorage.getItem(
        getCreativeDirectionSessionStorageKey(videoId)
      ) || null
    )
  } catch (err) {
    return null
  }
}

const createMessage = (role, content, extra = {}) => ({
  id: `${role}-${Date.now()}-${Math.random()}`,
  role,
  content,
  ...extra
})

const getFileExtension = fileName => {
  const parts = String(fileName || '').split('.')
  return parts.length > 1 ? parts.pop().toLowerCase() : ''
}

const isReferenceFileSupported = file => {
  const extension = getFileExtension(file?.name)
  return ACCEPTED_REFERENCE_EXTENSIONS.includes(extension)
}

const normalizeMovement = movement => {
  if (MOVEMENTS.some(item => item.key === movement)) return movement
  return 'the_read'
}

const buildInitialMetadata = creativeDirection => ({
  currentPhase: creativeDirection?.currentPhase || 'vision',
  currentMovement: normalizeMovement(creativeDirection?.currentMovement),
  movementsCovered: creativeDirection?.movementsCovered || [],
  movementsTotal: creativeDirection?.movementsTotal || MOVEMENTS.length
})

const getPhaseIndex = phase => {
  const index = PHASE_STEPS.findIndex(item => item.key === phase)
  return index === -1 ? 0 : index
}

const ENTITY_KIND_BY_PHASE = {
  characters: 'character',
  outfits: 'outfit',
  locations: 'location'
}

const ENTITY_LIST_KIND_BY_PHASE = {
  characters: 'characters',
  outfits: 'outfits',
  locations: 'locations'
}

const AGENT_EXTRACT_PROMPTS = {
  characters:
    'Button action: extract characters now. Do not ask a follow-up question and do not ask for confirmation. Immediately use the character tools and call add_character for every important character with the best available details. In this response, finish by showing the extracted character list for review. Do not verify or generate images yet.',
  outfits:
    'Button action: extract outfits now. Do not ask a follow-up question and do not ask for confirmation. Immediately use the outfit tools and call add_outfit for every important outfit with its character relationship and scene context. In this response, finish by showing the extracted outfit list for review. Do not verify or generate images yet.',
  locations:
    'Button action: extract locations now. Do not ask a follow-up question and do not ask for confirmation. Immediately use the location tools and call add_location for every important base location and needed variant. In this response, finish by showing the extracted location list for review. Do not verify or generate images yet.'
}

const AGENT_GENERATE_PROMPTS = {
  characters:
    'Button action: generate character images now. Do not ask a follow-up question and do not ask for confirmation. Immediately call the image generation tool for all verified characters in this phase. If generation runs in the background, return the queued task id/status in this response.',
  outfits:
    'Button action: generate outfit images now. Do not ask a follow-up question and do not ask for confirmation. Immediately call the image generation tool for all verified outfits in this phase. If generation runs in the background, return the queued task id/status in this response.',
  locations:
    'Button action: generate location images now. Do not ask a follow-up question and do not ask for confirmation. Immediately call the image generation tool for all verified locations in this phase. If generation runs in the background, return the queued task id/status in this response.',
  storyboard:
    'Button action: generate storyboard now. Do not ask a follow-up question and do not ask for confirmation. If the current storyboard plan is not approved, approve it now, then immediately call the storyboard image generation tool. If generation runs in the background, return the queued task id/status in this response.'
}

const AGENT_CONFIRM_GENERATE_PROMPTS = {
  characters:
    'Button action: confirm cast dynamics and generate character images now. Do not ask a follow-up question and do not ask for confirmation. Confirm the cast dynamics, then immediately call the image generation tool for all verified characters. If generation runs in the background, return the queued task id/status in this response.'
}

const AGENT_STORYBOARD_PLAN_PROMPT =
  'Button action: generate storyboard plan now. Do not ask a follow-up question and do not ask for confirmation. Immediately call propose_plan with the best coverage, pacing, locations, characters, and visual notes, then present the generated plan clearly for review in this response.'

const AGENT_VERIFY_ALL_PROMPTS = {
  characters:
    'Button action: verify all characters now. Do not ask a follow-up question and do not ask for confirmation. Immediately call verify_entity for every character in this phase and return the verification result in this response.',
  outfits:
    'Button action: verify all outfits now. Do not ask a follow-up question and do not ask for confirmation. Immediately call verify_entity for every outfit in this phase and return the verification result in this response.',
  locations:
    'Button action: verify all locations now. Do not ask a follow-up question and do not ask for confirmation. Immediately call verify_entity for every location in this phase and return the verification result in this response.'
}

const getAgentVerifyPrompt = (phase, entity) => {
  const entityId = getEntityId(entity)
  const name =
    entity?.name ||
    entity?.placeholder ||
    entity?.character_key ||
    entity?.outfit_key ||
    entity?.location_key ||
    `item ${entityId}`

  return `Button action: verify this item now. Do not ask a follow-up question and do not ask for confirmation. Immediately call verify_entity for ${name} (id: ${entityId}) in the ${phase} phase and return the verification result in this response.`
}

const getGeneratedAssetList = (data = {}) => {
  if (Array.isArray(data)) return data

  const candidates = [
    data.generated,
    data.assets,
    data.results,
    data.items,
    data.asset,
    data.generated?.generated,
    data.generated?.assets,
    data.output?.generated,
    data.output?.assets,
    data.output?.asset,
    data.data?.generated,
    data.data?.assets
  ]

  const found = candidates.find(Array.isArray)
  if (found) return found

  const single = candidates.find(
    item => item && typeof item === 'object' && !Array.isArray(item)
  )
  return single ? [single] : []
}

const getAssetSearchText = asset =>
  [
    asset?.asset_type,
    asset?.assetType,
    asset?.parent_entity_type,
    asset?.parentEntityType,
    asset?.kind,
    asset?.phase
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

const isGeneratedAssetForPhase = (asset, phase) => {
  const parentKind = getFirstString(
    asset?.parent_entity_type,
    asset?.parentEntityType,
    asset?.kind
  )

  if (phase === 'characters' && parentKind === 'character') return true
  if (phase === 'outfits' && parentKind === 'outfit') return true
  if (phase === 'locations' && parentKind === 'location') return true
  if (phase === 'storyboard' && parentKind === 'storyboard') return true

  const text = getAssetSearchText(asset)
  if (phase === 'characters') {
    return text.includes('character') || text.includes('emotion')
  }
  if (phase === 'outfits') {
    return text.includes('outfit') || text.includes('wardrobe')
  }
  if (phase === 'locations') {
    return text.includes('location') || text.includes('world')
  }
  if (phase === 'storyboard') {
    return text.includes('storyboard') || text.includes('panel')
  }

  return false
}

const filterGeneratedAssetsForPhase = (assets, phase) => {
  if (!Array.isArray(assets)) return []

  const filtered = assets.filter(asset => isGeneratedAssetForPhase(asset, phase))
  return filtered.length ? filtered : assets
}

const filterGeneratedAssetsForPhaseStrict = (assets, phase) => {
  if (!Array.isArray(assets)) return []
  return assets.filter(asset => isGeneratedAssetForPhase(asset, phase))
}

const getCreativeDirectionTaskId = (data = {}) =>
  data.task_id ||
  data.taskId ||
  data['task-id'] ||
  data.output?.task_id ||
  data.output?.taskId ||
  data.output?.['task-id'] ||
  null

const getAgentReply = data =>
  getFirstString(data?.reply, data?.message, data?.assistant_message)

const getAgentActions = data => {
  if (Array.isArray(data?.actions)) return data.actions
  if (Array.isArray(data?.tool_actions)) return data.tool_actions
  return []
}

const getAgentActionName = action =>
  getFirstString(
    action?.name,
    action?.tool,
    action?.action,
    action?.type,
    action?.event
  )

const getAgentActionInput = action => {
  if (!action || typeof action !== 'object') return {}
  if (action.input && typeof action.input === 'object') return action.input
  if (action.params && typeof action.params === 'object') return action.params
  if (action.arguments && typeof action.arguments === 'object') {
    return action.arguments
  }
  return {}
}

const getAgentActionResult = action => {
  if (!action || typeof action !== 'object') return {}
  if (action.result && typeof action.result === 'object') return action.result
  if (action.output && typeof action.output === 'object') return action.output
  if (action.data && typeof action.data === 'object') return action.data
  return action
}

const getAgentActionPhase = (action, fallbackPhase) => {
  const actionName = getAgentActionName(action)
  if (actionName === 'add_character') return 'characters'
  if (actionName === 'add_outfit') return 'outfits'
  if (actionName === 'add_location') return 'locations'

  const result = getAgentActionResult(action)
  const input = getAgentActionInput(action)
  const kind = getFirstString(
    result.phase,
    action?.phase,
    input.phase,
    result.kind,
    input.kind,
    action?.entity,
    result.parent_entity_type,
    result.parentEntityType,
    action?.kind
  )

  if (kind === 'character') return 'characters'
  if (kind === 'outfit') return 'outfits'
  if (kind === 'location') return 'locations'
  if (kind === 'storyboard') return 'storyboard'
  if (ENTITY_LIST_KIND_BY_PHASE[kind] || kind === 'storyboard') return kind

  return fallbackPhase
}

const getAgentActionTaskId = action => {
  const result = getAgentActionResult(action)

  return getCreativeDirectionTaskId(result) || getCreativeDirectionTaskId(action)
}

const isAgentReferenceRequest = action => {
  const actionName = getAgentActionName(action)
  const result = getAgentActionResult(action)

  return (
    actionName === 'reference_requested' ||
    actionName === 'request_reference' ||
    result.event === 'reference_requested' ||
    result.action === 'request_reference'
  )
}

const getEntityPhaseFromKind = kind => {
  if (kind === 'character') return 'characters'
  if (kind === 'outfit') return 'outfits'
  if (kind === 'location') return 'locations'
  return ''
}

const normalizeAgentReferenceRequest = (action, fallbackPhase) => {
  if (!isAgentReferenceRequest(action)) return null

  const input = getAgentActionInput(action)
  const result = getAgentActionResult(action)
  const kind = getFirstString(result.kind, input.kind, action?.kind)
  const entityId = getEntityIdFromActionPayload(input, result)
  const uploadPath = getFirstString(
    result.upload_path,
    result.uploadPath,
    input.upload_path,
    input.uploadPath,
    action?.upload_path,
    action?.uploadPath
  )
  const phase = getEntityPhaseFromKind(kind) || fallbackPhase
  const existingReferences =
    result.existing_references !== undefined &&
    result.existing_references !== null
      ? result.existing_references
      : result.existingReferences !== undefined &&
        result.existingReferences !== null
      ? result.existingReferences
      : input.existing_references !== undefined &&
        input.existing_references !== null
      ? input.existing_references
      : input.existingReferences !== undefined &&
        input.existingReferences !== null
      ? input.existingReferences
      : 0

  return {
    ...input,
    ...result,
    event: 'reference_requested',
    id: entityId,
    kind,
    phase,
    upload_path: uploadPath,
    uploadPath,
    name: getFirstString(result.name, input.name, action?.name),
    existing_references: existingReferences
  }
}

const getEntityIdFromActionPayload = (input = {}, result = {}) =>
  result.id || result.entity_id || result.entityId || input.id || input.entity_id || input.entityId

const getEntityKeyNameForPhase = phase => {
  if (phase === 'characters') return 'character_key'
  if (phase === 'outfits') return 'outfit_key'
  if (phase === 'locations') return 'location_key'
  return 'key'
}

const normalizeEntityFromAgentAction = (action, phase) => {
  const input = getAgentActionInput(action)
  const result = getAgentActionResult(action)
  const entityKeyName = getEntityKeyNameForPhase(phase)
  const entityKey = result.key || result[entityKeyName] || input[entityKeyName]
  const entity = {
    ...input,
    ...result,
    id: getEntityIdFromActionPayload(input, result),
    verified: Boolean(input.verified || result.verified),
    user_verified: Boolean(input.verified || result.verified)
  }

  if (entityKey) {
    entity[entityKeyName] = entityKey
  }

  return entity
}

const getEntityListFromPayload = (data = {}, phase) => {
  if (Array.isArray(data)) return data

  const output = data.output || {}
  const result = data.result || {}
  const phaseListKey = ENTITY_LIST_KIND_BY_PHASE[phase]
  const candidates = [
    data.entities,
    data[phase],
    data[phaseListKey],
    data.items,
    data.results,
    output.entities,
    output[phase],
    output[phaseListKey],
    result.entities,
    result[phase],
    result[phaseListKey],
    data.entities?.[phase],
    data.entities?.[phaseListKey],
    output.entities?.[phase],
    output.entities?.[phaseListKey],
    result.entities?.[phase],
    result.entities?.[phaseListKey]
  ]

  return candidates.find(Array.isArray) || null
}

const buildEntityPhaseUpdate = (entities, existingPhaseData = {}) => {
  const nextEntities = Array.isArray(entities) ? entities : []

  return {
    ...existingPhaseData,
    extracted: existingPhaseData.extracted || nextEntities.length > 0,
    count: nextEntities.length,
    entities: nextEntities,
    verified: nextEntities.length > 0 && nextEntities.every(isEntityVerified)
  }
}

const upsertEntity = (entities, entity) => {
  const entityId = getEntityId(entity)
  if (!entityId) return entities

  const entityIdText = String(entityId)
  const existingIndex = entities.findIndex(
    item => String(getEntityId(item)) === entityIdText
  )

  if (existingIndex === -1) {
    return [...entities, entity]
  }

  return entities.map((item, index) =>
    index === existingIndex
      ? {
          ...item,
          ...entity
        }
      : item
  )
}

const applyAgentEntityActionsToPhaseData = (prev, phaseActions, fallbackPhase) => {
  const next = { ...prev }

  phaseActions.forEach(({ action, phase }) => {
    const actionName = getAgentActionName(action)
    const input = getAgentActionInput(action)
    const result = getAgentActionResult(action)
    const phaseKey =
      phase ||
      getEntityPhaseFromKind(result.kind || input.kind) ||
      fallbackPhase

    if (!ENTITY_KIND_BY_PHASE[phaseKey]) return

    const currentPhaseData = next[phaseKey] || {}
    let entities = currentPhaseData.entities || []

    if (
      actionName === 'add_character' ||
      actionName === 'add_outfit' ||
      actionName === 'add_location' ||
      actionName === 'entity_detected'
    ) {
      entities = upsertEntity(
        entities,
        normalizeEntityFromAgentAction(action, phaseKey)
      )
    }

    if (actionName === 'verify_entity') {
      const entityId = getEntityIdFromActionPayload(input, result)
      const verified =
        result.verified !== undefined ? result.verified : input.verified

      entities = entities.map(entity =>
        String(getEntityId(entity)) === String(entityId)
          ? {
              ...entity,
              verified: Boolean(verified),
              user_verified: Boolean(verified)
            }
          : entity
      )
    }

    if (actionName === 'edit_entity') {
      const entityId = getEntityIdFromActionPayload(input, result)
      const updated = result.updated || input.updated || input.fields || {}

      entities = entities.map(entity =>
        String(getEntityId(entity)) === String(entityId)
          ? {
              ...entity,
              ...updated
            }
          : entity
      )
    }

    if (actionName === 'remove_entity') {
      const entityId = getEntityIdFromActionPayload(input, result)
      entities = entities.filter(
        entity => String(getEntityId(entity)) !== String(entityId)
      )
    }

    if (actionName === 'use_as_reference') {
      const entityId = getEntityIdFromActionPayload(input, result)
      const attached = Number(result.attached || 0)

      entities = entities.map(entity =>
        String(getEntityId(entity)) === String(entityId)
          ? {
              ...entity,
              refs: Number(entity.refs || entity.reference_count || 0) + attached,
              reference_count:
                Number(entity.reference_count || entity.refs || 0) + attached
            }
          : entity
      )
    }

    const castDynamicsUpdated =
      currentPhaseData.castDynamicsUpdated ||
      actionName === 'cast_dynamics_updated'

    next[phaseKey] = {
      ...currentPhaseData,
      extracted: currentPhaseData.extracted || entities.length > 0,
      count: entities.length,
      entities,
      verified: entities.length > 0 && entities.every(isEntityVerified),
      ...(castDynamicsUpdated
        ? {
            castDynamicsUpdated: true,
            dynamicsUpdated: true
          }
        : {})
    }
  })

  return next
}

const getStoryboardPlanActionPayload = action => {
  const result = getAgentActionResult(action)
  const input = getAgentActionInput(action)
  const plan =
    result.plan ||
    result.storyboard_plan ||
    result.storyboardPlan ||
    result.shot_plan ||
    result.shotPlan ||
    input.plan ||
    input.storyboard_plan ||
    input.storyboardPlan ||
    null

  // New agent-chat event format returns the shot rows directly in `plan`.
  // Normalize it to the existing storyboard plan shape without changing the
  // object-based formats handled below.
  if (Array.isArray(plan)) {
    return {
      approved: Boolean(result.approved),
      proposed_count:
        result.proposed_count || result.proposedCount || plan.length,
      rationale: result.rationale || result.summary || '',
      shots: plan
    }
  }

  if (plan && typeof plan === 'object') return plan

  const hasPlanShape =
    Array.isArray(result.shots) ||
    Array.isArray(result.panels) ||
    result.proposed_count ||
    result.proposedCount ||
    result.rationale ||
    result.summary ||
    result.narrative

  return hasPlanShape ? result : null
}

const getStoryboardCommittedPanelCount = action => {
  const result = getAgentActionResult(action)
  const input = getAgentActionInput(action)

  return (
    result.panels ||
    result.committed_panels ||
    result.committedPanels ||
    result.panel_count ||
    result.panelCount ||
    input.panels ||
    input.committed_panels ||
    input.committedPanels ||
    null
  )
}

const applyAgentStoryboardActionsToPhaseData = (prev, phaseActions) => {
  const storyboardActions = phaseActions.filter(item => {
    const actionName = getAgentActionName(item.action)
    return [
      'propose_plan',
      'update_plan',
      'plan_updated',
      'approve_plan',
      'generate_images',
      'edit_image',
      'edit_image_with_reference',
      'regenerate_image'
    ].includes(actionName)
  })

  if (!storyboardActions.length) return prev

  const currentStoryboard = prev.storyboard || {}
  let nextStoryboard = { ...currentStoryboard }
  let storyboardPlanApproved =
    prev.status?.storyboardPlanApproved ||
    currentStoryboard.planApproved ||
    false

  storyboardActions.forEach(({ action }) => {
    const actionName = getAgentActionName(action)
    const result = getAgentActionResult(action)

    if (
      actionName === 'propose_plan' ||
      actionName === 'update_plan' ||
      actionName === 'plan_updated'
    ) {
      const plan = getStoryboardPlanActionPayload(action)

      if (plan) {
        nextStoryboard = {
          ...nextStoryboard,
          plan,
          planApproved: Boolean(plan.approved) || storyboardPlanApproved,
          committedPanels:
            Boolean(plan.approved)
              ? getStoryboardCommittedPanelCount(action) ||
                nextStoryboard.committedPanels
              : nextStoryboard.committedPanels
        }
        storyboardPlanApproved =
          Boolean(plan.approved) || storyboardPlanApproved
      }
    }

    if (actionName === 'approve_plan') {
      const plan = getStoryboardPlanActionPayload(action)
      const committedPanels = getStoryboardCommittedPanelCount(action)

      nextStoryboard = {
        ...nextStoryboard,
        plan: plan || nextStoryboard.plan,
        planApproved: Boolean(result.approved !== false),
        committedPanels: committedPanels || nextStoryboard.committedPanels
      }
      storyboardPlanApproved = Boolean(result.approved !== false)
    }
  })

  return {
    ...prev,
    storyboard: nextStoryboard,
    status: {
      ...(prev.status || {}),
      storyboardPlanApproved
    }
  }
}

const getAgentUploads = data => {
  if (Array.isArray(data?.uploads)) return data.uploads
  if (Array.isArray(data?.uploaded)) return data.uploaded
  if (Array.isArray(data?.files)) return data.files
  return []
}

const getEntityId = entity =>
  entity?.id || entity?.entity_id || entity?.entityId || entity?.db_id

const isEntityVerified = entity =>
  Boolean(entity?.user_verified || entity?.verified || entity?.is_verified)

const getVisualStyleLabel = videoStyle => {
  if (!videoStyle) return 'Realistic'
  if (typeof videoStyle === 'string') return videoStyle
  return videoStyle.title || videoStyle.name || videoStyle.label || videoStyle.key || 'Realistic'
}

const getStoryMode = audioMode => {
  if (audioMode === 'dialogue') return 'dialogue'
  if (audioMode === 'dialogue_and_narration') return 'dialogue_and_narration'
  return 'narration'
}

const normalizeSavedMessages = messages => {
  if (!Array.isArray(messages)) return []

  return messages
    .map(item => {
      if (typeof item === 'string') {
        return {
          role: 'assistant',
          content: item
        }
      }

      if (!item || typeof item !== 'object') return null

      const role =
        item.role ||
        item.sender ||
        item.author ||
        item.type ||
        (item.is_user ? 'user' : 'assistant')
      const content = getFirstString(
        item.content,
        item.message,
        item.text,
        item.reply,
        item.assistant_message,
        item.assistantMessage
      )

      if (!content) return null

      return {
        ...item,
        role,
        content
      }
    })
    .filter(Boolean)
    .map((item, index) => ({
      ...createMessage(item.role || 'assistant', item.content, {
        id: item.id || `${item.role || 'assistant'}-${index}`,
        streaming: false,
        timestamp: item.timestamp,
        phase: item.phase,
        movement: item.movement,
        metadata: item.metadata,
        created_at: item.created_at || item.createdAt
      })
    }))
}

const getFirstString = (...values) => {
  const value = values.find(item => typeof item === 'string' && item.trim())
  return value ? value.trim() : ''
}

const getStatusVisionDocument = status => {
  const candidates = [
    status?.vision,
    status?.vision_status?.vision,
    status?.visionStatus?.vision,
    status?.vision_status,
    status?.visionStatus,
    status?.current_vision,
    status?.currentVision,
    status?.creative_vision,
    status?.creativeVision,
    status?.creative_direction?.vision,
    status?.creativeDirection?.vision
  ]
  const vision = candidates.find(Boolean)

  if (!vision) return null
  if (typeof vision === 'string') return { creative_statement: vision }
  if (typeof vision === 'object') return vision
  return null
}

const getStatusCreativeStatement = status => {
  const vision = getStatusVisionDocument(status)

  return getFirstString(
    vision?.creative_statement,
    vision?.creativeStatement,
    status?.creative_statement,
    status?.creativeStatement,
    status?.vision_creative_statement,
    status?.visionCreativeStatement,
    status?.vision_status?.creative_statement,
    status?.visionStatus?.creativeStatement
  )
}

const getStatusVisionLocked = status =>
  Boolean(
    status?.vision_locked ||
      status?.visionLocked ||
      status?.vision_status?.locked ||
      status?.visionStatus?.locked ||
      status?.phases?.vision
  )

const getObjectCandidate = (...values) =>
  values.find(item => item && typeof item === 'object' && !Array.isArray(item)) ||
  null

const getSessionIdFromFullSession = (data = {}, fallbackSessionId) => {
  const session = getObjectCandidate(
    data.session,
    data.creative_direction,
    data.creativeDirection
  )
  const candidates = [
    data.session_id,
    data.sessionId,
    data.id,
    session?.session_id,
    session?.sessionId,
    session?.id,
    fallbackSessionId
  ]
  const sessionIdCandidate = candidates.find(
    item => item !== undefined && item !== null && String(item).trim()
  )

  return sessionIdCandidate || null
}

const getStatusFromFullSession = (data = {}) => {
  const session = getObjectCandidate(
    data.session,
    data.creative_direction,
    data.creativeDirection
  )

  return (
    getObjectCandidate(
      data.status,
      data.session_status,
      data.sessionStatus,
      session?.status,
      session
    ) || data
  )
}

const getMessagesFromFullSession = (data = {}) => {
  const session = getObjectCandidate(
    data.session,
    data.creative_direction,
    data.creativeDirection
  )
  const candidates = [
    data.messages,
    data.chat_messages,
    data.chatMessages,
    data.agent_messages,
    data.agentMessages,
    data.conversation,
    data.transcript,
    session?.messages,
    session?.chat_messages,
    session?.chatMessages,
    session?.agent_messages,
    session?.agentMessages,
    session?.conversation,
    session?.transcript
  ]

  return candidates.find(Array.isArray) || []
}

const getEntitiesFromFullSession = (data = {}, phase) => {
  const phasePayload = data[phase]
  const phaseDataPayload = data.phase_data?.[phase] || data.phaseData?.[phase]
  const candidates = [
    Array.isArray(phasePayload) ? phasePayload : null,
    phasePayload?.entities,
    data.entities?.[phase],
    data.entities?.[ENTITY_LIST_KIND_BY_PHASE[phase]],
    phaseDataPayload?.entities
  ]

  return candidates.find(Array.isArray) || []
}

const getGeneratedAssetsFromFullSession = (data = {}, phase) => {
  const phasePayload = data[phase]
  const phaseDataPayload = data.phase_data?.[phase] || data.phaseData?.[phase]
  const candidates = [
    phasePayload?.generated,
    phasePayload?.assets,
    phasePayload?.visuals,
    data.entities?.[phase]?.generated,
    data.entities?.[phase]?.assets,
    phaseDataPayload?.generated,
    phaseDataPayload?.assets
  ]
  const phaseAssets = candidates.find(Array.isArray)

  if (phaseAssets?.length) return phaseAssets

  return filterGeneratedAssetsForPhaseStrict(getGeneratedAssetList(data), phase)
}

const getStoryboardPlanFromFullSession = (data = {}) =>
  data.storyboard_plan ||
  data.storyboardPlan ||
  data.storyboard?.plan ||
  data.phase_data?.storyboard?.plan ||
  data.phaseData?.storyboard?.plan ||
  data.plan ||
  null

const getBibleFromFullSession = (data = {}) =>
  data.bible ||
  data.creative_bible ||
  data.creativeBible ||
  data.final_bible ||
  data.finalBible ||
  null

const hasFinalizedGuidelineReport = report => {
  if (!report) return false
  if (typeof report === 'string') return Boolean(report.trim())
  if (Array.isArray(report)) return report.length > 0
  if (typeof report !== 'object') return Boolean(report)

  const ignoredKeys = ['status', 'current_phase', 'currentPhase', 'phase']
  return Object.keys(report).some(key => !ignoredKeys.includes(key))
}

const buildPhaseDataFromFullSession = (data = {}) => {
  const basePhaseData =
    getObjectCandidate(data.phase_data, data.phaseData) || {}
  const next = { ...basePhaseData }

  Object.keys(ENTITY_KIND_BY_PHASE).forEach(phase => {
    const existingPhaseData = next[phase] || {}
    const entities = getEntitiesFromFullSession(data, phase)
    const generated = getGeneratedAssetsFromFullSession(data, phase)

    if (!entities.length && !generated.length) return

    const nextEntities = entities.length
      ? entities
      : existingPhaseData.entities || []

    next[phase] = {
      ...existingPhaseData,
      extracted:
        existingPhaseData.extracted || Boolean(nextEntities.length),
      count: nextEntities.length || existingPhaseData.count || 0,
      entities: nextEntities,
      verified:
        nextEntities.length > 0
          ? nextEntities.every(isEntityVerified)
          : existingPhaseData.verified || false,
      generated: generated.length
        ? generated
        : existingPhaseData.generated || []
    }
  })

  const storyboardPlan = getStoryboardPlanFromFullSession(data)
  const storyboardAssets = getGeneratedAssetsFromFullSession(data, 'storyboard')

  if (storyboardPlan || storyboardAssets.length) {
    const storyboardSheets = data.storyboard_sheets || []
    const activeSheetIds = storyboardSheets
      .map(sheet => sheet.id)
      .filter(Boolean)

    next.storyboard = {
      ...(next.storyboard || {}),
      plan: storyboardPlan || next.storyboard?.plan,
      generated: storyboardAssets.length
        ? storyboardAssets
        : next.storyboard?.generated || [],
      activeSheetIds: activeSheetIds.length
        ? activeSheetIds
        : next.storyboard?.activeSheetIds || []
    }
  }

  return next
}

const useCreativeDirection = ({
  formData,
  setFormData,
  videoId,
  onValidationChange
}) => {
  const savedCreativeDirection = formData?.creativeDirection || {}
  const savedGuidelineReport = hasFinalizedGuidelineReport(
    savedCreativeDirection.bible
  )
    ? savedCreativeDirection.bible
    : null
  const fallbackStoredSessionId = getStoredCreativeDirectionSessionId(videoId)
  const [sessionId, setSessionId] = useState(
    savedCreativeDirection.sessionId || fallbackStoredSessionId || null
  )
  const [sessionStatus, setSessionStatus] = useState('idle')
  const [messages, setMessages] = useState(() =>
    normalizeSavedMessages(savedCreativeDirection.messages)
  )
  const [metadata, setMetadata] = useState(() =>
    buildInitialMetadata(savedCreativeDirection)
  )
  const [uploadedReferences, setUploadedReferences] = useState(
    savedCreativeDirection.uploadedReferences || []
  )
  const [phaseData, setPhaseData] = useState(
    savedCreativeDirection.phaseData || {}
  )
  const [creativeBible, setCreativeBible] = useState(
    savedGuidelineReport
  )
  const [creativeStatement, setCreativeStatement] = useState(
    savedCreativeDirection.creativeStatement || ''
  )
  const [isVisionLocked, setIsVisionLocked] = useState(
    Boolean(savedCreativeDirection.visionLocked)
  )
  const [isFinalized, setIsFinalized] = useState(
    Boolean(savedGuidelineReport)
  )
  const [isCreatingSession, setIsCreatingSession] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isSynthesizingVision, setIsSynthesizingVision] = useState(false)
  const [isUploadingReference, setIsUploadingReference] = useState(false)
  const [isLockingVision, setIsLockingVision] = useState(false)
  const [phaseBusyAction, setPhaseBusyAction] = useState('')
  const [error, setError] = useState('')
  const { startPolling: startGenerationPolling } = useTaskPoller({
    pollInterval: GENERATION_POLL_INTERVAL,
    continuePollingOnError: false
  })
  const {
    startPolling: startReferenceUploadPolling,
    stopPolling: stopReferenceUploadPolling,
    progress: referenceUploadProgress,
    status: referenceUploadStatus,
    message: referenceUploadMessage
  } = useTaskPoller({
    pollInterval: 2000,
    continuePollingOnError: false
  })
  const hasInitializedRef = useRef(false)
  const messagesRef = useRef(messages)
  const activeChatTurnRef = useRef(null)
  const stoppedChatTurnIdsRef = useRef({})
  const nextChatTurnIdRef = useRef(0)

  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  const selectedMovement = metadata.currentMovement || 'the_read'
  const progressPercent = useMemo(() => {
    if (isFinalized) return 100

    const currentPhaseIndex = getPhaseIndex(metadata.currentPhase)
    const total = PHASE_STEPS.length - 1

    return Math.min(95, Math.round((currentPhaseIndex / total) * 100))
  }, [isFinalized, metadata.currentPhase])

  const persistCreativeDirection = useCallback(
    nextData => {
      setFormData(prev => ({
        ...prev,
        creativeDirection: {
          ...(prev.creativeDirection || {}),
          ...nextData
        }
      }))
    },
    [setFormData]
  )

  useEffect(() => {
    onValidationChange && onValidationChange(true)
  }, [onValidationChange])

  useEffect(() => {
    persistCreativeDirection({
      sessionId,
      currentPhase: metadata.currentPhase,
      currentMovement: metadata.currentMovement,
      movementsCovered: metadata.movementsCovered,
      movementsTotal: metadata.movementsTotal,
      phaseData,
      finalized: isFinalized,
      bible: creativeBible,
      creativeStatement,
      visionLocked: isVisionLocked,
      messages,
      uploadedReferences
    })
  }, [
    creativeStatement,
    creativeBible,
    isFinalized,
    isVisionLocked,
    metadata,
    messages,
    phaseData,
    persistCreativeDirection,
    sessionId,
    uploadedReferences
  ])

  const appendUserMessage = useCallback((content, phase, movement) => {
    const userMsg = createMessage('user', content, {
      phase: phase || 'vision',
      movement: movement || 'the_read',
      metadata: null,
      created_at: new Date().toISOString()
    })
    setMessages(prev => {
      const next = [...prev, userMsg]
      messagesRef.current = next
      return next
    })
  }, [])

  const appendAssistantToken = useCallback((token, phase, movement) => {
    setMessages(prev => {
      const nextMessages = [...prev]
      const lastMessage = nextMessages[nextMessages.length - 1]
      let next

      if (lastMessage && lastMessage.role === 'assistant' && lastMessage.streaming) {
        nextMessages[nextMessages.length - 1] = {
          ...lastMessage,
          content: `${lastMessage.content || ''}${token}`
        }
        next = nextMessages
      } else {
        next = [
          ...nextMessages,
          createMessage('assistant', String(token || ''), {
            streaming: true,
            phase: phase || 'vision',
            movement: movement || 'the_read',
            metadata: null,
            created_at: new Date().toISOString()
          })
        ]
      }
      messagesRef.current = next
      return next
    })
  }, [])

  const markAssistantDone = useCallback(() => {
    setMessages(prev => {
      const nextMessages = prev.map((item, index) =>
        index === prev.length - 1 && item.role === 'assistant'
          ? { ...item, streaming: false }
          : item
      )
      const lastMessage = nextMessages[nextMessages.length - 1]
      let next

      if (
        lastMessage &&
        lastMessage.role === 'assistant' &&
        !String(lastMessage.content || '').trim()
      ) {
        next = nextMessages.slice(0, -1)
      } else {
        next = nextMessages
      }
      messagesRef.current = next
      return next
    })
  }, [])

  const beginChatTurn = useCallback(() => {
    const nextTurnId = nextChatTurnIdRef.current + 1
    nextChatTurnIdRef.current = nextTurnId
    activeChatTurnRef.current = nextTurnId
    delete stoppedChatTurnIdsRef.current[nextTurnId]
    return nextTurnId
  }, [])

  const isChatTurnStopped = useCallback(turnId => {
    if (!turnId) return false

    return (
      Boolean(stoppedChatTurnIdsRef.current[turnId]) ||
      (activeChatTurnRef.current &&
        activeChatTurnRef.current !== turnId)
    )
  }, [])

  const clearChatTurn = useCallback(turnId => {
    if (activeChatTurnRef.current === turnId) {
      activeChatTurnRef.current = null
    }

    delete stoppedChatTurnIdsRef.current[turnId]
  }, [])

  const stopCurrentAgentTurn = useCallback(() => {
    const activeTurnId = activeChatTurnRef.current

    if (!activeTurnId) {
      if (!isUploadingReference && !phaseBusyAction) return

      stopReferenceUploadPolling()
      setIsUploadingReference(false)
      setPhaseBusyAction('')
      setError('')
      message.info('Stopped the current AI request.')
      return
    }

    stoppedChatTurnIdsRef.current[activeTurnId] = true
    markAssistantDone()
    setIsStreaming(false)
    setIsUploadingReference(false)
    setPhaseBusyAction('')
    stopReferenceUploadPolling()
    setError('')
    message.info('Stopped the current AI request.')
  }, [
    isUploadingReference,
    markAssistantDone,
    phaseBusyAction,
    stopReferenceUploadPolling
  ])

  const appendAssistantMessage = useCallback((content, phase, movement) => {
    if (!String(content || '').trim()) return

    const assistantMsg = createMessage('assistant', content, {
      phase: phase || 'vision',
      movement: movement || 'the_read',
      metadata: null,
      created_at: new Date().toISOString()
    })

    setMessages(prev => {
      const next = [...prev, assistantMsg]
      messagesRef.current = next
      return next
    })
  }, [])

  const handleMetadata = useCallback(nextMetadata => {
    setMetadata(prev => ({
      currentPhase: nextMetadata.current_phase || prev.currentPhase || 'vision',
      currentMovement: normalizeMovement(
        nextMetadata.current_movement || prev.currentMovement
      ),
      movementsCovered:
        nextMetadata.movements_covered || prev.movementsCovered || [],
      movementsTotal:
        nextMetadata.movements_total || prev.movementsTotal || MOVEMENTS.length
    }))
  }, [])

  const updateCurrentPhase = useCallback(nextPhase => {
    setSessionStatus(nextPhase)
    setMetadata(prev => ({
      ...prev,
      currentPhase: nextPhase || prev.currentPhase
    }))
  }, [])

  const updatePhaseData = useCallback((phase, nextData) => {
    setPhaseData(prev => ({
      ...prev,
      [phase]: {
        ...(prev[phase] || {}),
        ...nextData
      }
    }))
  }, [])

  const refreshPhaseEntities = useCallback(
    async (phase, targetSessionId = sessionId) => {
      if (!targetSessionId || !ENTITY_KIND_BY_PHASE[phase]) return []

      try {
        const response = await getCreativeDirectionEntities(
          targetSessionId,
          ENTITY_KIND_BY_PHASE[phase]
        )
        const data = getResponseData(response)
        const entities = getEntityListFromPayload(data, phase)

        if (!Array.isArray(entities)) return []

        setPhaseData(prev => ({
          ...prev,
          [phase]: buildEntityPhaseUpdate(entities, prev[phase] || {})
        }))

        return entities
      } catch (err) {
        console.warn(`Failed to refresh ${phase} entities:`, err)
        return []
      }
    },
    [sessionId]
  )

  const applyStatusData = useCallback(data => {
    if (!data) return

    const currentPhase = data.current_phase || data.currentPhase
    const statusVision = getStatusVisionDocument(data)
    const statusCreativeStatement = getStatusCreativeStatement(data)
    const statusVisionLocked = getStatusVisionLocked(data)

    setSessionStatus(data.status || currentPhase || 'in_progress')
    setMetadata(prev => ({
      ...prev,
      currentPhase: currentPhase || prev.currentPhase,
      movementsCovered:
        data.vision_movements_covered ||
        data.visionMovementsCovered ||
        prev.movementsCovered,
      movementsTotal:
        data.vision_movements_total ||
        data.visionMovementsTotal ||
        prev.movementsTotal
    }))

    if ((currentPhase && currentPhase !== 'vision') || statusVisionLocked) {
      setIsVisionLocked(true)
    }

    if (statusCreativeStatement) {
      setCreativeStatement(statusCreativeStatement)
    }

    setPhaseData(prev => {
      const storyboardPlanApproved =
        data.storyboard_plan_approved !== undefined &&
        data.storyboard_plan_approved !== null
          ? data.storyboard_plan_approved
          : data.storyboardPlanApproved !== undefined &&
            data.storyboardPlanApproved !== null
          ? data.storyboardPlanApproved
          : prev.status?.storyboardPlanApproved || false
      const nextPhaseData = {
        ...prev,
        status: {
          ...(prev.status || {}),
          raw: data,
          phases: data.phases || prev.status?.phases || {},
          counts: data.counts || prev.status?.counts || {},
          storyboardPlanApproved
        }
      }

      if (statusVision || statusCreativeStatement || statusVisionLocked) {
        nextPhaseData.vision = {
          ...(prev.vision || {}),
          status: statusVision || prev.vision?.status || null,
          creativeStatement:
            statusCreativeStatement || prev.vision?.creativeStatement || '',
          locked: statusVisionLocked || prev.vision?.locked || false
        }
      }

      return nextPhaseData
    })
  }, [])

  const refreshStatus = useCallback(
    async activeSessionId => {
      if (!activeSessionId) return null

      try {
        const response = await getCreativeDirectionSessionStatus(
          activeSessionId
        )
        const data = getResponseData(response)
        applyStatusData(data)
        return data
      } catch (err) {
        console.warn('Failed to refresh creative direction status:', err)
        return null
      }
    },
    [applyStatusData]
  )

  const hydrateFullSession = useCallback(
    (data, fallbackSessionId) => {
      if (!data) return

      const fullSessionId = getSessionIdFromFullSession(
        data,
        fallbackSessionId || sessionId
      )
      const fullStatus = getStatusFromFullSession(data)
      const fullMessages = normalizeSavedMessages(
        getMessagesFromFullSession(data)
      )
      const fullPhaseData = buildPhaseDataFromFullSession(data)
      const fullCreativeStatement =
        getStatusCreativeStatement(fullStatus) || getStatusCreativeStatement(data)
      const fullBible = getBibleFromFullSession(data)

      if (fullSessionId) {
        setSessionId(fullSessionId)
      }

      applyStatusData(fullStatus)

      if (fullMessages.length) {
        setMessages(fullMessages)
        messagesRef.current = fullMessages
      }

      if (Object.keys(fullPhaseData).length) {
        setPhaseData(prev => ({
          ...prev,
          ...fullPhaseData
        }))
      }

      if (fullCreativeStatement) {
        setCreativeStatement(fullCreativeStatement)
      }

      if (hasFinalizedGuidelineReport(fullBible)) {
        setCreativeBible(fullBible)
        setIsFinalized(true)
      } else {
        setCreativeBible(null)
        setIsFinalized(false)
      }
    },
    [applyStatusData, sessionId]
  )

  const loadStoredSession = useCallback(
    async storedSessionId => {
      if (!storedSessionId) return false

      setIsCreatingSession(true)
      setError('')

      try {
        const response = await getCreativeDirectionSessionFull(storedSessionId)
        const data = getResponseData(response)
        hydrateFullSession(data, storedSessionId)
        await refreshStatus(storedSessionId)
        return true
      } catch (err) {
        console.warn('Failed to restore creative direction session:', err)
        setSessionId(null)
        return false
      } finally {
        setIsCreatingSession(false)
      }
    },
    [hydrateFullSession, refreshStatus]
  )

  const processAgentActions = useCallback(
    async (actions, phase, turnId = null) => {
      if (!sessionId) return
      if (isChatTurnStopped(turnId)) return

      if (!Array.isArray(actions) || !actions.length) {
        if (!isChatTurnStopped(turnId)) {
          await refreshStatus(sessionId)
        }
        return
      }

      const phaseActions = actions.map(action => ({
        action,
        result: getAgentActionResult(action),
        phase: getAgentActionPhase(action, phase)
      }))
      const entityPhasesToRefresh = [
        ...new Set(
          phaseActions
            .map(item => item.phase || phase)
            .filter(item => ENTITY_KIND_BY_PHASE[item])
        )
      ]

      phaseActions.forEach(item => {
        const referenceRequest = normalizeAgentReferenceRequest(
          item.action,
          item.phase || phase
        )

        if (referenceRequest) {
          updatePhaseData(referenceRequest.phase || item.phase || phase, {
            referenceRequest
          })
        }
      })

      if (!isChatTurnStopped(turnId)) {
        setPhaseData(prev =>
          applyAgentStoryboardActionsToPhaseData(
            applyAgentEntityActionsToPhaseData(prev, phaseActions, phase),
            phaseActions
          )
        )
      }

      for (const entityPhase of entityPhasesToRefresh) {
        if (isChatTurnStopped(turnId)) return
        await refreshPhaseEntities(entityPhase, sessionId)
      }

      const queuedTasks = phaseActions
        .map(item => ({
          phase: item.phase || phase,
          taskId: getAgentActionTaskId(item.action)
        }))
        .filter(item => item.taskId)

      for (const task of queuedTasks) {
        if (isChatTurnStopped(turnId)) return

        updatePhaseData(task.phase, {
          generationTask: {
            taskId: task.taskId,
            phase: task.phase,
            status: 'pending',
            startedAt: new Date().toISOString()
          }
        })

        try {
          const generationResult = await startGenerationPolling(task.taskId)
          if (isChatTurnStopped(turnId)) return

          const generationOutput = generationResult?.output || generationResult
          const generatedAssets = filterGeneratedAssetsForPhase(
            getGeneratedAssetList(generationOutput),
            task.phase
          )

          updatePhaseData(task.phase, {
            generated: generatedAssets,
            generationTask: {
              taskId: task.taskId,
              phase: task.phase,
              status: 'success',
              completedAt: new Date().toISOString()
            }
          })
        } catch (err) {
          if (isChatTurnStopped(turnId)) return

          const rawError =
            err?.response?.data?.detail ||
            err?.message ||
            'Image generation failed'
          const errorMessage = getFriendlyErrorMessage(rawError, task.phase)

          updatePhaseData(task.phase, {
            generationTask: {
              taskId: task.taskId,
              phase: task.phase,
              status: 'failed',
              error: errorMessage,
              failedAt: new Date().toISOString()
            }
          })
          setError(errorMessage)
          message.error(errorMessage)
        }
      }

      if (!isChatTurnStopped(turnId)) {
        await refreshStatus(sessionId)
      }
    },
    [
      isChatTurnStopped,
      refreshPhaseEntities,
      refreshStatus,
      sessionId,
      startGenerationPolling,
      updatePhaseData
    ]
  )

  const runAgentTurn = useCallback(
    async ({
      prompt,
      phase,
      files = [],
      displayText,
      busyKey,
      fallbackReply = 'Done. I updated this step.'
    }) => {
      const cleanText = String(prompt || '').trim()
      const activePhase = phase || metadata.currentPhase || 'characters'

      if (
        !sessionId ||
        !cleanText ||
        isStreaming ||
        isSynthesizingVision ||
        isLockingVision ||
        phaseBusyAction
      ) {
        return null
      }

      if (
        files.some(
          file => !isReferenceFileSupported(file) || file.size > MAX_REFERENCE_FILE_SIZE
        )
      ) {
        message.error('Attach image or video files up to 50 MB.')
        return null
      }

      let didSetPhaseBusyAction = false

      if (busyKey) {
        setPhaseBusyAction(busyKey)
        didSetPhaseBusyAction = true
      }
      const turnId = beginChatTurn()
      setError('')
      appendUserMessage(displayText || cleanText, activePhase, selectedMovement)
      setIsStreaming(true)

      try {
        let uploads = []

        if (files.length) {
          setIsUploadingReference(true)
          const uploadResponse = await uploadCreativeDirectionUploads(
            sessionId,
            files
          )
          if (isChatTurnStopped(turnId)) return null

          const uploadData = getResponseData(uploadResponse)
          uploads = getAgentUploads(uploadData)
          setUploadedReferences(prev => [
            ...prev,
            ...uploads.map(upload => ({
              upload_id: upload.upload_id,
              url: upload.url,
              filename: upload.filename
            }))
          ])
          setIsUploadingReference(false)
        }

        const response = await sendCreativeDirectionAgentChat(
          sessionId,
          cleanText,
          uploads
        )
        if (isChatTurnStopped(turnId)) return null

        const data = getResponseData(response)
        const responsePhase = data.phase || activePhase
        const reply = getAgentReply(data)
        const agentActions = getAgentActions(data)
        const actions =
          agentActions.length || !getCreativeDirectionTaskId(data)
            ? agentActions
            : [{ result: data }]
        const responseEntities = getEntityListFromPayload(data, responsePhase)
        const hasQueuedTask = actions.some(action =>
          getAgentActionTaskId(action)
        )

        if (!busyKey && hasQueuedTask) {
          setPhaseBusyAction(`generate:${responsePhase}`)
          didSetPhaseBusyAction = true
        }

        appendAssistantMessage(reply || fallbackReply, responsePhase, selectedMovement)
        setPhaseData(prev => {
          const existingPhaseData = prev[responsePhase] || {}
          const entityUpdate = Array.isArray(responseEntities)
            ? buildEntityPhaseUpdate(responseEntities, existingPhaseData)
            : existingPhaseData

          return {
            ...prev,
            [responsePhase]: {
              ...entityUpdate,
              lastAgentActions: actions
            }
          }
        })
        setIsStreaming(false)
        await processAgentActions(actions, responsePhase, turnId)

        if (isChatTurnStopped(turnId)) return null

        return data
      } catch (err) {
        if (isChatTurnStopped(turnId)) return null

        const rawError =
          err?.response?.data?.detail ||
          err?.message ||
          'Creative direction agent failed'
        const errorMessage = getFriendlyErrorMessage(rawError, activePhase)
        setError(errorMessage)
        message.error(errorMessage)
        return null
      } finally {
        if (activeChatTurnRef.current === turnId) {
          setIsStreaming(false)
          setIsUploadingReference(false)
          if (didSetPhaseBusyAction) setPhaseBusyAction('')
        }
        clearChatTurn(turnId)
      }
    },
    [
      appendAssistantMessage,
      appendUserMessage,
      beginChatTurn,
      clearChatTurn,
      isChatTurnStopped,
      isLockingVision,
      isStreaming,
      isSynthesizingVision,
      metadata.currentPhase,
      phaseBusyAction,
      processAgentActions,
      selectedMovement,
      sessionId,
      updatePhaseData
    ]
  )

  const createSession = useCallback(async (options = {}) => {
    const force = Boolean(options && options.force)

    if (!force && (hasInitializedRef.current || sessionId)) return

    const storyText = String(formData?.voiceoverText || '').trim()
    if (!storyText) {
      setError('Add the story or narration before starting creative direction.')
      return
    }

    hasInitializedRef.current = true
    setIsCreatingSession(true)
    setError('')

    try {
      const response = await createCreativeDirectionSession({
        story_text: storyText,
        story_mode: getStoryMode(formData?.audioMode),
        canvas_style: formData?.aspectRatio || '16:9',
        visual_style: getVisualStyleLabel(formData?.videoStyle),
        video_id: videoId ? String(videoId) : undefined
      })
      const data = getResponseData(response)
      const nextSessionId = getSessionIdFromFullSession(data)

      setSessionId(nextSessionId)
      applyStatusData(data)
      await refreshStatus(nextSessionId)
    } catch (err) {
      hasInitializedRef.current = false
      const rawError = err?.message || 'Unable to start creative direction'
      const errorMessage = getFriendlyErrorMessage(rawError, 'initialize')
      setError(errorMessage)
      message.error(errorMessage)
    } finally {
      setIsCreatingSession(false)
    }
  }, [
    formData?.aspectRatio,
    formData?.audioMode,
    formData?.videoStyle,
    formData?.voiceoverText,
    applyStatusData,
    refreshStatus,
    sessionId,
    videoId
  ])

  useEffect(() => {
    let isCancelled = false

    const initializeSession = async () => {
      if (hasInitializedRef.current) return

      const storedSessionId =
        savedCreativeDirection.sessionId ||
        getStoredCreativeDirectionSessionId(videoId)

      if (storedSessionId) {
        hasInitializedRef.current = true
        const restored = await loadStoredSession(storedSessionId)

        if (isCancelled || restored) return

        hasInitializedRef.current = false
        await createSession({ force: true })
        return
      }

      await createSession()
    }

    initializeSession()

    return () => {
      isCancelled = true
    }
  }, [
    createSession,
    loadStoredSession,
    savedCreativeDirection.sessionId,
    videoId
  ])

  const runStream = useCallback(
    async streamRunner => {
      const turnId = beginChatTurn()
      setIsStreaming(true)
      setError('')

      try {
        await streamRunner({
          onToken: token => {
            if (!isChatTurnStopped(turnId)) {
              appendAssistantToken(token, metadata.currentPhase, selectedMovement)
            }
          },
          onMetadata: nextMetadata => {
            if (!isChatTurnStopped(turnId)) {
              handleMetadata(nextMetadata)
            }
          },
          onDone: () => {
            if (!isChatTurnStopped(turnId)) {
              markAssistantDone()
            }
          },
          onError: errorMessage => {
            if (isChatTurnStopped(turnId)) return
            const friendly = getFriendlyErrorMessage(errorMessage, metadata.currentPhase)
            setError(friendly)
            message.error(friendly)
          },
          movement: selectedMovement
        })

        return !isChatTurnStopped(turnId)
      } catch (err) {
        if (isChatTurnStopped(turnId)) return false

        markAssistantDone()
        const rawError = err?.message || 'Creative direction stream failed'
        const errorMessage = getFriendlyErrorMessage(rawError, metadata.currentPhase)
        setError(errorMessage)
        message.error(errorMessage)
        return false
      } finally {
        if (activeChatTurnRef.current === turnId) {
          setIsStreaming(false)
        }
        clearChatTurn(turnId)
      }
    },
    [
      appendAssistantToken,
      beginChatTurn,
      clearChatTurn,
      handleMetadata,
      isChatTurnStopped,
      markAssistantDone,
      metadata.currentPhase,
      selectedMovement
    ]
  )

  const synthesizeVision = useCallback(
    async ({ showError = false } = {}) => {
      if (!sessionId) return null

      setIsSynthesizingVision(true)
      setError('')

      try {
        const chatPayload = messagesRef.current
          .filter(msg => msg && msg.content && !msg.streaming)
          .map(msg => ({
            role: msg.role || 'assistant',
            content: msg.content,
            phase: msg.phase || 'vision',
            movement: msg.movement || 'the_read',
            metadata: msg.metadata || null,
            created_at: msg.created_at || msg.timestamp || new Date().toISOString()
          }))
        const response = await synthesizeCreativeDirectionVision(sessionId, chatPayload)
        const data = getResponseData(response)
        const vision = data.vision || getStatusVisionDocument(data) || {}
        const statement = getFirstString(
          getStatusCreativeStatement(data),
          getStatusCreativeStatement({ vision })
        )
        const visionPayload =
          vision && typeof vision === 'object'
            ? {
                ...vision,
                creative_statement: getFirstString(
                  vision.creative_statement,
                  vision.creativeStatement,
                  statement
                )
              }
            : { creative_statement: statement || String(vision || '') }

        applyStatusData({
          vision: visionPayload,
          creative_statement: statement
        })
        await refreshStatus(sessionId)

        if (!statement && showError) {
          message.error('Vision synthesis returned no creative statement.')
        }

        return { vision: visionPayload, statement }
      } catch (err) {
        const rawError =
          err?.response?.data?.detail ||
          err?.message ||
          'Unable to synthesize vision'
        const errorMessage = getFriendlyErrorMessage(rawError, 'vision')

        setError(errorMessage)
        if (showError) {
          message.error(errorMessage)
        }

        return null
      } finally {
        setIsSynthesizingVision(false)
      }
    },
    [applyStatusData, refreshStatus, sessionId]
  )

  const sendMessage = useCallback(
    async (text, options = {}) => {
      const cleanText = String(text || '').trim()
      if (
        !sessionId ||
        !cleanText ||
        isStreaming ||
        isSynthesizingVision ||
        isLockingVision ||
        phaseBusyAction
      ) {
        return
      }
      const files = Array.isArray(options.files)
        ? options.files.filter(Boolean)
        : []
      const phase = metadata.currentPhase || 'vision'

      if (
        files.some(
          file => !isReferenceFileSupported(file) || file.size > MAX_REFERENCE_FILE_SIZE
        )
      ) {
        message.error('Attach image or video files up to 50 MB.')
        return
      }

      if (phase !== 'vision') {
        await runAgentTurn({
          prompt: cleanText,
          phase,
          files
        })
        return
      }

      if (files.length) {
        setIsUploadingReference(true)
        setError('')

        try {
          for (const file of files) {
            const response = await uploadCreativeDirectionReference(
              sessionId,
              file
            )
            const data = getResponseData(response)
            setUploadedReferences(prev => [
              ...prev,
              {
                asset_id: data.asset_id,
                kind: data.kind,
                image_url: data.image_url,
                analysis: data.analysis,
                filename: file.name
              }
            ])
          }
        } catch (err) {
          const rawError = err?.message || 'Reference upload failed'
          const errorMessage = getFriendlyErrorMessage(rawError, 'reference_upload')
          setError(errorMessage)
          message.error(errorMessage)
          setIsUploadingReference(false)
          return
        } finally {
          setIsUploadingReference(false)
        }
      }

      appendUserMessage(cleanText, phase, selectedMovement)
      const didComplete = await runStream(handlers =>
        streamCreativeDirectionChat(sessionId, cleanText, handlers)
      )

      if (didComplete) {
        await refreshStatus(sessionId)
      }
    },
    [
      appendUserMessage,
      isLockingVision,
      isSynthesizingVision,
      isStreaming,
      metadata.currentPhase,
      phaseBusyAction,
      runAgentTurn,
      runStream,
      sessionId,
      selectedMovement
    ]
  )

  const uploadReference = useCallback(
    async (file, options = {}) => {
      if (!sessionId || !file || isUploadingReference) return false
      const shouldShowUploadInChat = !options.silent

      if (!isReferenceFileSupported(file)) {
        message.error('Upload an image or video reference file.')
        return false
      }

      if (file.size > MAX_REFERENCE_FILE_SIZE) {
        message.error('Reference files must be 50 MB or less.')
        return false
      }

      setIsUploadingReference(true)
      setError('')

      try {
        const response = await uploadCreativeDirectionReference(sessionId, file)
        const data = getResponseData(response)
        setUploadedReferences(prev => [
          ...prev,
          {
            asset_id: data.asset_id,
            kind: data.kind,
            image_url: data.image_url,
            analysis: data.analysis,
            filename: file.name
          }
        ])
        if (shouldShowUploadInChat) {
          appendUserMessage(`Uploaded reference: ${file.name}`, metadata.currentPhase, selectedMovement)
        }

        if (data.analysis && shouldShowUploadInChat) {
          const assistantMsg = createMessage('assistant', data.analysis, {
            phase: metadata.currentPhase || 'vision',
            movement: selectedMovement,
            metadata: null,
            created_at: new Date().toISOString()
          })
          setMessages(prev => {
            const next = [...prev, assistantMsg]
            messagesRef.current = next
            return next
          })
        }
        await refreshStatus(sessionId)
      } catch (err) {
        const rawError = err?.message || 'Reference upload failed'
        const errorMessage = getFriendlyErrorMessage(rawError, 'reference_upload')
        setError(errorMessage)
        message.error(errorMessage)
      } finally {
        setIsUploadingReference(false)
      }

      return false
    },
    [
      appendUserMessage,
      isUploadingReference,
      metadata.currentPhase,
      refreshStatus,
      selectedMovement,
      sessionId
    ]
  )

  const uploadRequestedReference = useCallback(
    async (phase, referenceRequest, files) => {
      const request = referenceRequest || {}
      const uploadPath = getFirstString(request.upload_path, request.uploadPath)
      const requestPhase =
        phase || request.phase || getEntityPhaseFromKind(request.kind) || metadata.currentPhase
      const requestId = request.id || request.entity_id || request.entityId
      const requestName =
        request.name ||
        request.placeholder ||
        request.character_key ||
        request.outfit_key ||
        request.location_key ||
        'this item'
      const busyKey = `reference:${requestPhase}:${requestId || requestName}`
      const selectedFiles = (Array.isArray(files) ? files : [files])
        .map(item => item && (item.originFileObj || item))
        .filter(Boolean)
      const fileNames = selectedFiles
        .map(item => item.name)
        .filter(Boolean)
      const fileLabel =
        fileNames.length === 1 ? fileNames[0] : `${selectedFiles.length} files`

      if (
        !sessionId ||
        !selectedFiles.length ||
        !uploadPath ||
        isUploadingReference ||
        phaseBusyAction
      ) {
        return false
      }

      if (selectedFiles.some(item => !isReferenceFileSupported(item))) {
        message.error('Upload an image or video reference file.')
        return false
      }

      if (selectedFiles.some(item => item.size > MAX_REFERENCE_FILE_SIZE)) {
        message.error('Reference files must be 50 MB or less.')
        return false
      }

      setIsUploadingReference(true)
      setPhaseBusyAction(busyKey)
      setError('')
      updatePhaseData(requestPhase, {
        referenceRequest: request,
        referenceUploadTask: {
          status: 'uploading',
          progress: 0,
          filename: fileLabel,
          filenames: fileNames,
          entityId: requestId,
          name: requestName,
          startedAt: new Date().toISOString()
        }
      })
      appendUserMessage(
        selectedFiles.length === 1
          ? `Uploaded reference for ${requestName}: ${fileLabel}`
          : `Uploaded ${selectedFiles.length} references for ${requestName}.`,
        requestPhase,
        selectedMovement
      )

      try {
        const uploadResponse = await uploadCreativeDirectionReferenceToPath(
          uploadPath,
          selectedFiles
        )
        const uploadData = getResponseData(uploadResponse)
        const taskId = getCreativeDirectionTaskId(uploadData)
        const uploadRecord = {
          ...(uploadData && !Array.isArray(uploadData) ? uploadData : {}),
          response: uploadData,
          task_id: taskId || uploadData.task_id,
          upload_path: uploadPath,
          kind: request.kind,
          entity_id: requestId,
          name: requestName,
          filename: fileLabel,
          filenames: fileNames
        }

        setUploadedReferences(prev => [...prev, uploadRecord])

        let resolvedData = uploadData

        if (taskId) {
          updatePhaseData(requestPhase, {
            referenceUploadTask: {
              taskId,
              status: 'processing',
              progress: 1,
              filename: fileLabel,
              filenames: fileNames,
              entityId: requestId,
              name: requestName,
              startedAt: new Date().toISOString()
            }
          })
          resolvedData = await startReferenceUploadPolling(taskId)
        }

        const outputData = resolvedData?.output || resolvedData || {}
        const responsePhase = outputData.phase || requestPhase
        const reply = getAgentReply(outputData)
        const actions = getAgentActions(outputData)
        const responseEntities = Array.isArray(outputData)
          ? null
          : getEntityListFromPayload(outputData, responsePhase)
        const uploadedReferenceAssets = Array.isArray(outputData)
          ? outputData
          : getGeneratedAssetList(outputData)
        const uploadedCount =
          uploadedReferenceAssets.length || selectedFiles.length || 1

        appendAssistantMessage(
          reply ||
            (uploadedCount === 1
              ? `Reference image uploaded for ${requestName}.`
              : `${uploadedCount} reference images uploaded for ${requestName}.`),
          responsePhase,
          selectedMovement
        )

        setPhaseData(prev => {
          const existingPhaseData = prev[responsePhase] || {}
          const entities = Array.isArray(responseEntities)
            ? responseEntities
            : (existingPhaseData.entities || []).map(entity =>
                String(getEntityId(entity)) === String(requestId)
                  ? {
                      ...entity,
                      refs:
                        Number(entity.refs || entity.reference_count || 0) +
                        uploadedCount,
                      reference_count:
                        Number(entity.reference_count || entity.refs || 0) +
                        uploadedCount
                    }
                  : entity
              )
          const entityUpdate = Array.isArray(responseEntities)
            ? buildEntityPhaseUpdate(responseEntities, existingPhaseData)
            : {
                ...existingPhaseData,
                entities
              }

          return {
            ...prev,
            [responsePhase]: {
              ...entityUpdate,
              referenceRequest: null,
              referenceUploadTask: {
                taskId,
                status: 'success',
                progress: 100,
                filename: fileLabel,
                filenames: fileNames,
                entityId: requestId,
                name: requestName,
                uploadedAssets: uploadedReferenceAssets,
                completedAt: new Date().toISOString()
              },
              referenceUploads: [
                ...(existingPhaseData.referenceUploads || []),
                ...uploadedReferenceAssets
              ],
              lastAgentActions: actions.length
                ? actions
                : existingPhaseData.lastAgentActions
            }
          }
        })

        await processAgentActions(actions, responsePhase)
        await refreshPhaseEntities(responsePhase, sessionId)
        await refreshStatus(sessionId)
        message.success(
          uploadedCount === 1
            ? `Reference uploaded for ${requestName}.`
            : `${uploadedCount} references uploaded for ${requestName}.`
        )
      } catch (err) {
        const rawError =
          err?.response?.data?.detail ||
          err?.message ||
          'Reference upload failed'
        const errorMessage = getFriendlyErrorMessage(rawError, 'reference_upload')
        updatePhaseData(requestPhase, {
          referenceUploadTask: {
            status: 'failed',
            error: errorMessage,
            filename: fileLabel,
            filenames: fileNames,
            entityId: requestId,
            name: requestName,
            failedAt: new Date().toISOString()
          }
        })
        setError(errorMessage)
        message.error(errorMessage)
      } finally {
        setIsUploadingReference(false)
        setPhaseBusyAction('')
      }

      return false
    },
    [
      appendAssistantMessage,
      appendUserMessage,
      isUploadingReference,
      metadata.currentPhase,
      phaseBusyAction,
      processAgentActions,
      refreshPhaseEntities,
      refreshStatus,
      selectedMovement,
      sessionId,
      startReferenceUploadPolling,
      updatePhaseData
    ]
  )

  const changeMovement = useCallback(nextMovement => {
    setMetadata(prev => ({
      ...prev,
      currentMovement: normalizeMovement(nextMovement)
    }))
  }, [])

  const startPrompt = useCallback(() => {
    if (messages.length || isStreaming || !sessionId) return
    sendMessage(
      'Please interview me about the creative direction for this video before we build the shot list.'
    )
  }, [isStreaming, messages.length, sendMessage, sessionId])

  const lockVisionAndContinue = useCallback(async () => {
    if (!sessionId || isStreaming || isLockingVision) return

    if (!messages.some(item => String(item.content || '').trim())) {
      message.error('Chat with the AI before confirming the vision.')
      return
    }

    setIsLockingVision(true)
    setError('')

    try {
      let vision = phaseData.vision?.status || null
      let statement = getFirstString(
        getStatusCreativeStatement({ vision }),
        phaseData.vision?.creativeStatement,
        creativeStatement
      )

      if (!statement) {
        message.error('Create a vision statement before locking vision.')
        return
      }

      const visionPayload =
        vision && typeof vision === 'object'
          ? {
              ...vision,
              creative_statement: getFirstString(
                vision.creative_statement,
                vision.creativeStatement,
                statement
              )
            }
          : { creative_statement: statement }
      await saveCreativeDirectionVision(sessionId, visionPayload)
      applyStatusData({
        vision: visionPayload,
        creative_statement: statement
      })
      await refreshStatus(sessionId)

      const response = await advanceCreativeDirectionSession(sessionId)
      const data = getResponseData(response)
      const nextPhase = data.current_phase || 'characters'

      applyStatusData(data)
      await refreshStatus(sessionId)
      setIsVisionLocked(true)
      persistCreativeDirection({
        currentPhase: nextPhase,
        creativeStatement: statement,
        vision: visionPayload,
        visionLocked: true
      })
      message.success('Creative vision locked.')
    } catch (err) {
      const rawError =
        err?.response?.data?.detail ||
        err?.message ||
        'Unable to lock creative vision'
      const errorMessage = getFriendlyErrorMessage(rawError, 'vision')
      setError(errorMessage)
      message.error(errorMessage)
    } finally {
      setIsLockingVision(false)
    }
  }, [
    isLockingVision,
    isStreaming,
    applyStatusData,
    creativeStatement,
    messages,
    phaseData.vision,
    persistCreativeDirection,
    refreshStatus,
    sessionId
  ])

  const extractCurrentPhase = useCallback(async () => {
    const phase = metadata.currentPhase
    if (!sessionId || phase === 'vision' || phase === 'finalized') return

    const prompt = AGENT_EXTRACT_PROMPTS[phase]
    if (!prompt) {
      message.error(`Unable to extract ${phase}.`)
      return
    }

    await runAgentTurn({
      prompt,
      phase,
      displayText: `Extract ${phase}.`,
      busyKey: `extract:${phase}`,
      fallbackReply: `I extracted the ${phase}. Please review each item.`
    })
  }, [metadata.currentPhase, runAgentTurn, sessionId])

  const verifyCurrentPhaseEntity = useCallback(
    async (phase, entity) => {
      const currentPhase = phase || metadata.currentPhase
      const entityId = getEntityId(entity)

      if (!sessionId || !ENTITY_KIND_BY_PHASE[currentPhase] || !entityId) {
        message.error('Unable to verify this item.')
        return
      }

      const entityName =
        entity?.name ||
        entity?.placeholder ||
        entity?.character_key ||
        entity?.outfit_key ||
        entity?.location_key ||
        `item ${entityId}`

      await runAgentTurn({
        prompt: getAgentVerifyPrompt(currentPhase, entity),
        phase: currentPhase,
        displayText: `Verify ${entityName}.`,
        busyKey: `verify:${currentPhase}:${entityId}`,
        fallbackReply: `${entityName} is verified.`
      })
    },
    [metadata.currentPhase, runAgentTurn, sessionId]
  )

  const verifyCurrentPhaseAll = useCallback(
    async phase => {
      const currentPhase = phase || metadata.currentPhase
      const prompt = AGENT_VERIFY_ALL_PROMPTS[currentPhase]

      if (!sessionId || !prompt) {
        message.error(`Unable to verify ${currentPhase}.`)
        return
      }

      const entities = phaseData[currentPhase]?.entities || []
      if (!entities.length) {
        message.error(`Extract ${currentPhase} before verifying.`)
        return
      }

      await runAgentTurn({
        prompt,
        phase: currentPhase,
        displayText: `Verify all ${currentPhase}.`,
        busyKey: `verify-all:${currentPhase}`,
        fallbackReply: `All ${currentPhase} are verified.`
      })
    },
    [metadata.currentPhase, phaseData, runAgentTurn, sessionId]
  )

  const generateCurrentPhase = useCallback(async phaseOverride => {
    const requestedPhase =
      typeof phaseOverride === 'string' ? phaseOverride : null
    const phase = requestedPhase || metadata.currentPhase

    if (!sessionId || phase === 'vision' || phase === 'finalized') return
    const entities = phaseData[phase]?.entities || []
    const isEntityPhase = Boolean(ENTITY_KIND_BY_PHASE[phase])

    if (isEntityPhase && entities.length && !entities.every(isEntityVerified)) {
      message.error(`Verify every ${phase} item before generating visuals.`)
      return
    }

    const hasCastDynamicsReady =
      phase === 'characters' && phaseData[phase]?.castDynamicsUpdated
    const prompt =
      hasCastDynamicsReady && AGENT_CONFIRM_GENERATE_PROMPTS[phase]
        ? AGENT_CONFIRM_GENERATE_PROMPTS[phase]
        : AGENT_GENERATE_PROMPTS[phase]
    if (!prompt) {
      message.error(`Unable to generate ${phase}.`)
      return
    }

    await runAgentTurn({
      prompt,
      phase,
      displayText: hasCastDynamicsReady
        ? 'Confirm cast dynamics and generate character visuals.'
        : `Generate ${phase} visuals.`,
      busyKey: `generate:${phase}`,
      fallbackReply: `I queued the ${phase} visuals.`
    })
  }, [
    metadata.currentPhase,
    phaseData,
    runAgentTurn,
    sessionId
  ])

  const generateStoryboardPlan = useCallback(async () => {
    if (!sessionId || metadata.currentPhase !== 'storyboard') return

    await runAgentTurn({
      prompt: AGENT_STORYBOARD_PLAN_PROMPT,
      phase: 'storyboard',
      displayText: 'Extract storyboard plan.',
      busyKey: 'storyboard:plan',
      fallbackReply: 'I prepared the storyboard plan for review.'
    })
  }, [metadata.currentPhase, runAgentTurn, sessionId])

  const refreshGenerationStatus = useCallback(async () => {
    if (!sessionId) return
    const phase = metadata.currentPhase

    setPhaseBusyAction('generation-status')
    setError('')

    try {
      const response = await getCreativeDirectionGenerationStatus(sessionId)
      const data = getResponseData(response)
      const generatedAssets = filterGeneratedAssetsForPhase(
        getGeneratedAssetList(data),
        phase
      )
      updatePhaseData(phase, {
        generated: generatedAssets
      })
      await refreshStatus(sessionId)
    } catch (err) {
      const rawError =
        err?.response?.data?.detail ||
        err?.message ||
        'Unable to refresh generation status'
      const errorMessage = getFriendlyErrorMessage(rawError, phase)
      setError(errorMessage)
      message.error(errorMessage)
    } finally {
      setPhaseBusyAction('')
    }
  }, [metadata.currentPhase, refreshStatus, sessionId, updatePhaseData])

  const advanceCurrentPhase = useCallback(async () => {
    const phase = metadata.currentPhase
    if (!sessionId || phase === 'vision' || phase === 'finalized') return

    setPhaseBusyAction(`advance:${phase}`)
    setError('')

    try {
      const response = await advanceCreativeDirectionSession(sessionId)
      const data = getResponseData(response)
      const nextPhase = data.current_phase || phase
      applyStatusData(data)
      updateCurrentPhase(nextPhase)
      message.success(`Moved to ${nextPhase}.`)
      await refreshStatus(sessionId)
    } catch (err) {
      const rawError =
        err?.response?.data?.detail ||
        err?.message ||
        `Unable to advance from ${phase}`
      const errorMessage = getFriendlyErrorMessage(rawError, phase)
      setError(errorMessage)
      message.error(errorMessage)
    } finally {
      setPhaseBusyAction('')
    }
  }, [
    applyStatusData,
    metadata.currentPhase,
    refreshStatus,
    sessionId,
    updateCurrentPhase
  ])

  const finalizeCreativeDirection = useCallback(async () => {
    if (!sessionId) return

    const phase = metadata.currentPhase

    if (phase !== 'finalized') {
      message.error(
        'Continue to Creative Guideline before generating the finalized report.'
      )
      return
    }

    setPhaseBusyAction('finalize')
    setError('')

    try {
      const response = await finalizeCreativeDirectionSession(sessionId)
      const data = getResponseData(response)
      setCreativeBible(data)
      setIsFinalized(true)
      applyStatusData({
        status: 'finalized',
        current_phase: 'finalized'
      })
      updateCurrentPhase('finalized')
      await refreshStatus(sessionId)
      persistCreativeDirection({
        currentPhase: 'finalized',
        bible: data,
        finalized: true
      })
      message.success('Creative guideline generated.')
    } catch (err) {
      const rawError =
        err?.response?.data?.detail ||
        err?.message ||
        'Unable to finalize Creative Direction'
      const errorMessage = getFriendlyErrorMessage(rawError, 'finalized')
      setError(errorMessage)
      message.error(errorMessage)
    } finally {
      setPhaseBusyAction('')
    }
  }, [
    applyStatusData,
    metadata.currentPhase,
    persistCreativeDirection,
    refreshStatus,
    sessionId,
    updateCurrentPhase
  ])

  return {
    sessionId,
    sessionStatus,
    messages,
    metadata,
    phaseSteps: PHASE_STEPS,
    phaseData,
    progressPercent,
    movements: MOVEMENTS,
    uploadedReferences,
    creativeStatement,
    isVisionLocked,
    isFinalized,
    creativeBible,
    acceptedExtensions: ACCEPTED_REFERENCE_EXTENSIONS,
    selectedMovement,
    isCreatingSession,
    isStreaming,
    isSynthesizingVision,
    isUploadingReference,
    referenceUploadProgress,
    referenceUploadStatus,
    referenceUploadMessage,
    isLockingVision,
    phaseBusyAction,
    error,
    stopCurrentAgentTurn,
    sendMessage,
    uploadReference,
    uploadRequestedReference,
    changeMovement,
    lockVisionAndContinue,
    synthesizeVision,
    extractCurrentPhase,
    verifyCurrentPhaseEntity,
    verifyCurrentPhaseAll,
    generateCurrentPhase,
    generateStoryboardPlan,
    refreshGenerationStatus,
    advanceCurrentPhase,
    finalizeCreativeDirection,
    startPrompt,
    retryCreateSession: createSession
  }
}

export default useCreativeDirection
